import { Col, Row } from "react-bootstrap";
import StationPicker from "../../components/Pickers/StationPicker";
import { axiosInstance } from "../../auth/Auth";
import { useEffect, useMemo, useRef, useState } from "react";
import useSortableData from "../../components/useSortableData";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useUserFormState } from "../../components/UserSelection";
import { useEditableList } from "../../components/HelperHooks";
import PlaylistAutopilotSettingsCategoryHeader from "../../components/playlistAutopilot/PlaylistAutopilotSettingsCategoryHeader";
import PlaylistAutopilotSettingsCategoryRow from "../../components/playlistAutopilot/PlaylistAutopilotSettingsCategoryRow";
import { Outlet } from "react-router-dom";
import PlaylistAutopilotSettingsRulesModal from "../../components/playlistAutopilot/PlaylistAutopilotSettingsRulesModal";
import EmptyRow from "../../components/EmptyRow";
import { VirtualizedWindowContainer } from "../../components/pageSections";

export default function SettingsPlaylistAutopilot() {
    const moduleName = "settingsPlaylistAutopilot";

    const [formData, setFormData, saveFormData] = useUserFormState(moduleName);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [setupIssues, setSetupIssues] = useState([]);
    const [setupIssuesLoading, setSetupIssuesLoading] = useState(false);
    const [selectedRadioId, setSelectedRadioId] = useState(null);
    const initialData = useMemo(() => data ?? [], [data]);

    const [selectedCategoryRules, setSelectedCategoryRules] = useState(null);

    const tableContainerRef = useRef(null);

    useEffect(() => {
        if (formData.station.length < 1) {
            setSelectedRadioId(null);
            return;
        }
        const radioId = formData.station[0].radio_id;
        if (selectedRadioId === radioId) return;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const { data } = await axiosInstance.get(`playlist_autopilot/categories/categories?radio_id=${radioId}`);
                if (!cancelled) {
                    setData(data);
                    setSelectedRadioId(radioId);
                    await saveFormData();
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load categories", err);
                    setData(null);
                    setSelectedRadioId(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => (cancelled = true);
    }, [formData.station, saveFormData, selectedRadioId]);

    useEffect(() => {
        if (!selectedRadioId) return;

        let cancelled = false;
        setSetupIssuesLoading(true);

        (async () => {
            try {
                const { data } = await axiosInstance.get(`playlist_autopilot/categories/setup_issues?radio_id=${selectedRadioId}`);
                if (!cancelled) setSetupIssues(data);
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load setup issues", err);
                    setSetupIssues([]);
                }
            } finally {
                if (!cancelled) setSetupIssuesLoading(false);
            }
        })();

        return () => (cancelled = true);
    }, [selectedRadioId]);

    const newItemTemplate = useMemo(
        () => ({
            radio_id: data?.length > 0 ? data[0].radio_id : formData.station[0]?.radio_id,
            code: "",
            name: "",
            min_year: null,
            max_year: null,
            relative_min_age: null,
            relative_max_age: null,
            size_type: "unlimited",
            target_size: null,
            flexibility_size_absolute: null,
            flexibility_size_percentage: null,
            move_up_code: null,
            move_down_code: null,
            move_out_code: null,
        }),
        [data, formData.station],
    );

    const isInvalid = item => !(item.code && item.name && item.size_type && (item.size_type === "target" ? item.target_size : true));

    const handleCreate = async item => {
        if (isInvalid(item)) throw new Error("Invalid payload");
        const newId = (await axiosInstance.post("playlist_autopilot/categories/categories", item)).data;
        return { ...item, category_id: newId };
    };

    const handleUpdate = async item => {
        if (isInvalid(item)) throw new Error("Invalid payload");
        await axiosInstance.put(`playlist_autopilot/categories/categories/${item.category_id}`, item);
    };

    const { items: categoriesCopy, handlers } = useEditableList({
        initialItems: initialData,
        idKey: "category_id",
        itemName: "category",
        createTempItem: (curr, props) => ({
            status: "ADD",
            category_id: -(curr.length + 1),
            ...props,
        }),
        createItem: handleCreate,
        updateItem: handleUpdate,
        deleteItem: async ({ category_id }) => await axiosInstance.delete(`playlist_autopilot/categories/categories/${category_id}`),
    });

    const { sortedItems, requestSort, sortConfig } = useSortableData(categoriesCopy, { key: "category_id", direction: "ascending" });

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: () => 42,
        overscan: 20,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <>
            <Row>
                <Col className="bg-white p-4">
                    <h2 className="text-uppercase">Categories</h2>
                    <Row>
                        <StationPicker
                            formData={formData}
                            setFormData={setFormData}
                            size="lg"
                            name={moduleName}
                            moduleFilterFunction={x => x.module_id === 15}
                        />
                    </Row>
                    <hr />
                    {loading ? (
                        <span colSpan={12}>
                            <LoadingIndicator width={75} />
                        </span>
                    ) : (
                        data && (
                            <>
                                <PlaylistAutopilotSettingsRulesModal
                                    categories={categoriesCopy}
                                    categoryId={selectedCategoryRules?.category_id}
                                    onClose={() => setSelectedCategoryRules(null)}
                                />
                                <div className=" overflow-x-auto">
                                    <PlaylistAutopilotSettingsCategoryHeader
                                        minWidth={1400}
                                        sortConfig={sortConfig}
                                        requestSort={requestSort}
                                        onCreate={() => handlers.add(newItemTemplate)}
                                    />
                                    <VirtualizedWindowContainer minWidth={1400} containerRef={tableContainerRef} virtualizer={virtualizer}>
                                        {virtualizer.getVirtualItems().map(virtualRow => {
                                            const item = sortedItems[virtualRow.index];
                                            if (!item) return <EmptyRow key={virtualRow.key} text="No Custom Categories" />;
                                            return (
                                                <PlaylistAutopilotSettingsCategoryRow
                                                    key={item.category_id}
                                                    data-index={virtualRow.index}
                                                    virtualizer={virtualizer}
                                                    style={{ transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)` }}
                                                    item={item}
                                                    setupIssues={setupIssues.find(x => x.category_id === item.category_id)?.issues ?? []}
                                                    categories={categoriesCopy.filter(c => c.category_id !== item.category_id)}
                                                    dataHandlers={handlers}
                                                    onRulesClick={() => setSelectedCategoryRules(item)}
                                                />
                                            );
                                        })}
                                    </VirtualizedWindowContainer>
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
            <Outlet />
        </>
    );
}
